import clsx from "clsx";
import { FC, ReactNode } from "react";
import { Asset } from "rujira.js";
import {
  Decimal,
  Icons,
  LoaderWithContent,
  SimulationState,
  useTranslation,
} from "rujira.ui";
import { usePaysNetworkFee } from "../../../common/components/TxButton";
import { LiquidationAccountData, LiquidationPrice, LtvGraph } from "../Health";
import { Cdp } from "../utils";

const Stat: FC<{
  label: ReactNode;
  align?: "left" | "right";
  gap?: string;
  children?: ReactNode;
}> = ({ label, align = "left", gap = "gap-0.5", children }) => (
  <div
    className={clsx(
      "flex dir-c",
      gap,
      align === "right" ? "ai-e text-right ml-a" : "ai-s"
    )}>
    <span className="mobile-card__label">{label}</span>
    {children}
  </div>
);

export const ManagePositionSummary: FC<{
  p: Cdp<Asset>;
  liquidationAccount: LiquidationAccountData;
  liquidationLabel: string;
  onOrientationSet: (orientation: "collateral" | "debt") => void;
  projectedRate?: bigint;
  simulationState?: SimulationState;
  originalDebtAmount?: bigint;
  hideInterestRate?: boolean;
}> = ({
  p,
  liquidationAccount,
  liquidationLabel,
  onOrientationSet,
  projectedRate,
  simulationState,
  originalDebtAmount,
  hideInterestRate,
}) => {
  const { t } = useTranslation();
  const paysNetworkFee = usePaysNetworkFee();
  const networkFee = simulationState?.simulation?.amount ?? 0n;
  const hasDebt = p.debts[0].amount > 0n;
  const feeValue = (
    <LoaderWithContent
      loading={simulationState?.status === "running"}
      content={
        <Decimal
          subscript
          className={clsx("fs-12", { "color-grey": networkFee === 0n })}
          amount={networkFee}
          decimals={simulationState?.simulation?.decimals}
          symbol={simulationState?.simulation?.symbol ?? "RUNE"}
        />
      }
    />
  );

  return (
    <div className="flex dir-c gap-2 condensed fs-14">
      {hasDebt ? (
        <div className="flex ai-s jc-sb gap-2">
          <Stat label={liquidationLabel} gap="gap-1">
            <LiquidationPrice
              p={p}
              account={liquidationAccount}
              onOrientationSet={(orientation) => onOrientationSet(orientation)}
            />
          </Stat>

          <Stat
            label={
              <span
                data-tooltip-id="modal-tip"
                data-tooltip-html={t("ltvTooltip")}
                className="iflex ai-c no-select">
                {t("newLtv")}
                <Icons.Info className="ml-0.5 block w-1.5" />
              </span>
            }
            align="right">
            <LtvGraph ltv={p.ltv} size={2.75} decimals={0} />
          </Stat>
        </div>
      ) : (
        <div className="borrow__notice">
          {originalDebtAmount === 0n
            ? t("paidOffReopenMessage")
            : t("liquidationPriceNotApplicable")}
        </div>
      )}

      {hasDebt && !hideInterestRate ? (
        <div className="flex ai-s jc-sb gap-2">
          <Stat label={t("newInterestRate")}>
            <div className="tag tag--borderless tag--index">
              {(
                Number(projectedRate ?? p.debts[0].rate) / 1e10
              ).toLocaleDecimal(2)}
              %
            </div>
          </Stat>

          {paysNetworkFee && (
            <Stat label={t("common:networkFee")} align="right">
              <div className="flex ai-c h-3">{feeValue}</div>
            </Stat>
          )}
        </div>
      ) : (
        paysNetworkFee && (
          <div className="borrow__fee-row flex ai-e jc-sb gap-2 mx-0.5">
            <span className="mobile-card__label">{t("common:networkFee")}</span>
            {feeValue}
          </div>
        )
      )}
    </div>
  );
};
